import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { addFavorite, removeFavorite, getFavorites } from '../services/customerApi';
import toast from 'react-hot-toast';

const FavoriteButton = ({ venueId, venueName, venueSlug, onAuthRequired, size = 'large' }) => {
  const { isAuthenticated } = useAuth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      checkFavoriteStatus();
    }
  }, [isAuthenticated, venueId]);

  const checkFavoriteStatus = async () => {
    try {
      const favorites = await getFavorites();
      const favorite = favorites.find(f => f.venueId === venueId);
      if (favorite) {
        setIsFavorite(true);
        setFavoriteId(favorite.id);
      }
    } catch (error) {
      console.error('Error checking favorite:', error);
    }
  };

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      if (onAuthRequired) {
        onAuthRequired();
      } else {
        toast.error('Favorilere eklemek için giriş yapın');
      }
      return;
    }

    setLoading(true);
    try {
      if (isFavorite && favoriteId) {
        await removeFavorite(favoriteId);
        setIsFavorite(false);
        setFavoriteId(null);
        toast.success('Favorilerden kaldırıldı');
      } else {
        const result = await addFavorite(venueId);
        setIsFavorite(true);
        setFavoriteId(result.id);
        toast.success('Favorilere eklendi');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setLoading(false);
    }
  };

  // Size variants
  const sizeClasses = {
    small: {
      button: 'p-2 rounded-lg shadow-md',
      icon: 'w-4 h-4',
      showText: false
    },
    large: {
      button: 'px-6 py-3 rounded-full shadow-lg hover:shadow-xl',
      icon: 'w-5 h-5',
      showText: true
    }
  };

  const currentSize = sizeClasses[size] || sizeClasses.large;

  return (
    <button
      onClick={handleToggleFavorite}
      disabled={loading}
      className={`
        group relative font-medium text-sm
        transition-all duration-300 transform hover:scale-110
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
        flex items-center justify-center gap-2
        ${currentSize.button}
        ${
          isFavorite
            ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:from-pink-600 hover:to-rose-600'
            : 'bg-white/95 backdrop-blur-sm text-gray-700 hover:bg-white border border-white/20 hover:border-pink-300'
        }
      `}
      title={isFavorite ? 'Favorilerden çıkar' : 'Favorilere ekle'}
    >
      <Heart
        className={`${currentSize.icon} transition-all duration-300 ${
          isFavorite 
            ? 'fill-current' 
            : 'group-hover:fill-pink-200'
        }`}
      />
      {currentSize.showText && (
        <span className="font-semibold">
          {loading ? 'İşleniyor...' : isFavorite ? 'Favorilerimde' : 'Favorilerime Ekle'}
        </span>
      )}
      {isFavorite && size === 'large' && (
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-pink-500"></span>
        </span>
      )}
    </button>
  );
};

export default FavoriteButton;